<template>
  <SmDialog v-model:visibility="visibility" :title="i18n.t('deleteUser')">
    <PeopleCardSmall :person="props.person" />
    <div class="spacing" />

    <SmDialogMessage
      :type="message.type"
      :message="message.message"
      :visible="message.visible" />

    <div v-if="props.person.application_count > 0" class="my-3">
      <h3 class="mb-3">{{ i18n.t('account', { count: 2 }) }}</h3>

      <PeopleDeleteLicenceTable
        ref="peopleDeleteLicenseTable"
        :person="props.person" />
    </div>

    <template #footer>
      <span class="dialog-footer">
        <!-- Cancel -->
        <SmButton
          v-if="!loading && !completed"
          outline
          @click="visibility = false"
          >{{ i18n.t('general.cancel') }}
        </SmButton>
        <!-- Close -->
        <SmButton v-if="completed" type="primary" outline @click="closeDialog"
          >{{ i18n.t('general.close') }}
        </SmButton>
        <!-- Handle Delete -->
        <SmButton
          v-if="!completed"
          type="primary"
          :loading="loading"
          :disabled="
            props.person.application_count > 0 &&
            !peopleDeleteLicenseTable?.allLicensesSelected
          "
          @click="
            props.person.application_count > 0
              ? handleAccounts()
              : deletePerson()
          ">
          {{ i18n.t('delete') }}
        </SmButton>
      </span>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'

  import PeopleDeleteLicenceTable from './PeopleDeleteLicenceTable.vue'
  import { DialogMessageTypes } from '../sm/SmDialogMessage.vue'
  import { EnrichedAccount, PersonStore } from '@/stores/person/person.store'

  const i18n = useI18n()

  const peopleDeleteLicenseTable =
    ref<ComponentExposed<typeof PeopleDeleteLicenceTable>>()

  const visibility = defineModel<boolean>('visibility', {
    default: true,
  })

  const props = defineProps({
    person: {
      type: Object as PropType<EnrichedAccount>,
      required: true,
    },
  })

  // Deletion
  const loading = ref(false)
  const completed = ref(false)

  async function handleAccounts() {
    loading.value = true
    const res = await peopleDeleteLicenseTable.value?.excecute()

    if (res) {
      completed.value = true
    }

    loading.value = false
  }

  async function deletePerson() {
    loading.value = true

    try {
      await PersonStore.deletePerson(props.person._id)
      completed.value = true
    } finally {
      loading.value = false
    }
  }

  function closeDialog() {
    if (peopleDeleteLicenseTable.value?.results)
      peopleDeleteLicenseTable.value.results = {}
    completed.value = false
    visibility.value = false
  }

  // Message
  const message: ComputedRef<{
    type: DialogMessageTypes
    message: string
    visible: boolean
  }> = computed(() => {
    // If actions are executed
    if (completed.value) {
      // If all users are deleted
      if (
        props.person.application_count === 0 ||
        peopleDeleteLicenseTable.value?.allLicenseDeletionsSuccessful
      ) {
        return {
          type: 'success',
          message: i18n.t('deletionSuccessful'),
          visible: true,
        }
      } else {
        return {
          type: 'error',
          message: i18n.t('licensesDeletionNotSuccessful'),
          visible: true,
        }
      }
    }

    if (
      !peopleDeleteLicenseTable.value?.allLicensesSelected &&
      peopleDeleteLicenseTable.value?.licenseTable?.licences &&
      peopleDeleteLicenseTable.value?.licenseTable?.licences.length > 0
    ) {
      return {
        type: 'warning',
        message: i18n.t('userDeleteNotAllLicensesDeleted'),
        visible: true,
      }
    } else {
      return {
        type: 'success',
        message: '',
        visible: false,
      }
    }
  })
</script>
